import React, { useEffect, useState } from "react";
import { Button, Header, Icon, Table, Pagination, Segment, Label, Grid, Input, Modal } from "semantic-ui-react";
import { toast } from "react-toastify";
import useOperationTypeService from "../../../services/backoffice/hooks/useOperationTypeService"; // Serviço de Operation Type
import { ToasterConfig } from "../../../config/ToasterConfig";
import TooltipInfo from "../../tooltip/tooltip";
import { SessionUser } from "../../../models/session-user";
import SessionStorage from "../../../store/session";
import { useNavigate } from "react-router-dom";
import OperationTypeForm from "./create-operation-type-form";
import {OperationType} from "../../../models/fleet-type";
import {OperationTypeFilter} from "../../../filters/operation-type-filter"; // Formulário de criação/edição de Operation Type

interface OperationTypesListProps {
    isModal?: boolean;
    open?: boolean;
    onClose?: () => void;
}

const OperationTypesList: React.FC<OperationTypesListProps> = ({ isModal = false, open = false, onClose }) => {
    const [operationTypes, setOperationTypes] = useState<OperationType[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [filter, setFilter] = useState<OperationTypeFilter>({ Page: 1 });
    const [totalPages, setTotalPages] = useState<number>(0);
    const { getOperationTypes, deleteOperationType, activateOperationType, deactivateOperationType } = useOperationTypeService();
    const [sessionUser, setSessionUser] = useState<SessionUser>();
    const navigate = useNavigate();
    const [showFilters, setShowFilters] = useState(false);

    const [modalOpen, setModalOpen] = useState(false);
    const [editOperationTypeId, setEditOperationTypeId] = useState<string | null>(null);

    const toggleFilters = () => {
        setShowFilters(!showFilters);
    };

    useEffect(() => {
        async function updateSessionUser() {
            const user = await SessionStorage.getSession();
            setSessionUser(user);
        }
        updateSessionUser();
    }, []);

    useEffect(() => {
        async function loadOperationTypes() {
            setLoading(true);
            try {
                const response = await getOperationTypes(filter);
                setOperationTypes(response.itens);
                setTotalPages(response.totalPages);
            } catch (err: any) {
                setError("Erro ao carregar tipos de operação.");
            } finally {
                setLoading(false);
            }
        }
        loadOperationTypes();
    }, [filter]);

    const handleDelete = async (id: string) => {
        try {
            const result = window.confirm("Tem certeza que deseja excluir este tipo de operação?");
            if (result) {
                await deleteOperationType(id);
                toast.success("Tipo de operação excluído com sucesso", ToasterConfig.success);
                setOperationTypes(operationTypes.filter((op) => op.id !== id));
            }
        } catch (err: any) {
            toast.error("Erro ao excluir tipo de operação", ToasterConfig.error);
        }
    };

    const handleActivation = async (operationType: OperationType) => {
        try {
            const result = window.confirm(`Tem certeza que deseja ${operationType.isActive ? 'desativar' : 'ativar'} este tipo de operação?`);
            if (result) {
                if (operationType.isActive) {
                    await deactivateOperationType(operationType.id);
                } else {
                    await activateOperationType(operationType.id);
                }
                setFilter({ ...filter }); // Atualiza a lista
                toast.success(`Tipo de operação ${operationType.isActive ? 'desativado' : 'ativado'} com sucesso.`, ToasterConfig.success);
            }
        } catch (err: any) {
            toast.error("Erro ao alterar status do tipo de operação", ToasterConfig.error);
        }
    };

    const handleEdit = (id: string) => {
        setEditOperationTypeId(id);
        setModalOpen(true);
    };

    // Formulário de tipos de operação com botão de "Novo"
    const formContent = (
        <Segment raised style={{ maxWidth: "900px", margin: "5px auto", padding: "20px" }}>
            <Header as="h3" textAlign="center" color={'green'}>
                Tipos de Operação
            </Header>
            <Table.Row>
                <Table.HeaderCell>
                    {sessionUser?.userData?.profile === 'Admin' && (
                        <Button color="green" onClick={() => setModalOpen(true)}>
                            <Icon name='plus' /> Novo
                        </Button>
                    )}
                </Table.HeaderCell>
                <Table.HeaderCell colSpan="4">
                    <Button onClick={toggleFilters} color="green">
                        <Icon name={showFilters ? 'hide' : 'unhide'} />
                        {showFilters ? 'Ocultar filtros' : 'Exibir filtros'}
                    </Button>
                </Table.HeaderCell>
            </Table.Row>

            {showFilters && (
                <Segment>
                    <Grid stackable doubling padded>
                        <Grid.Row>
                            <Grid.Column width={6}>
                                <label htmlFor="code">Código</label>
                                <Input
                                    fluid
                                    id="code"
                                    icon="search"
                                    type="text"
                                    placeholder="Código do tipo de operação"
                                    value={filter?.code ?? ''}
                                    onChange={(event) => setFilter({ ...filter, code: event.target.value })}
                                />
                            </Grid.Column>

                            <Grid.Column width={6}>
                                <label htmlFor="name">Nome do Tipo de Operação</label>
                                <Input
                                    fluid
                                    id="name"
                                    icon="search"
                                    type="text"
                                    placeholder="Nome do tipo de operação"
                                    value={filter?.name ?? ''}
                                    onChange={(event) => setFilter({ ...filter, name: event.target.value })}
                                />
                            </Grid.Column>

                            <Grid.Column width={2} verticalAlign="bottom">
                                <Button color="green" onClick={() => setFilter({ ...filter, Page: 1 })}>Buscar</Button>
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </Segment>
            )}

            <Table color="green">
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>#</Table.HeaderCell>
                        <Table.HeaderCell>Código</Table.HeaderCell>
                        <Table.HeaderCell>Nome</Table.HeaderCell>
                        <Table.HeaderCell>Ativo</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {operationTypes?.map((operationType) => (
                        <Table.Row key={operationType.id}>
                            <Table.Cell>
                                <Button.Group>
                                    {sessionUser?.userData?.profile === 'Admin' && (
                                        <TooltipInfo
                                            content="Editar tipo de operação"
                                            trigger={
                                                <Button
                                                    color="green"
                                                    onClick={() => {
                                                        setEditOperationTypeId(operationType.id);
                                                        setModalOpen(true);
                                                    }}
                                                    icon="edit"
                                                />
                                            }
                                        />
                                    )}

                                    {sessionUser?.userData?.profile === 'Admin' && (
                                        <TooltipInfo
                                            content="Excluir tipo de operação"
                                            trigger={
                                                <Button
                                                    color="red"
                                                    onClick={() => handleDelete(operationType.id)}
                                                    icon="trash"
                                                />
                                            }
                                        />
                                    )}

                                    {sessionUser?.userData?.profile === 'Admin' && (
                                        <TooltipInfo
                                            content={operationType.isActive ? "Desativar tipo de operação" : "Ativar tipo de operação"}
                                            trigger={
                                                <Button
                                                    color={operationType.isActive ? 'red' : 'green'}
                                                    onClick={() => handleActivation(operationType)}
                                                    icon={operationType.isActive ? 'power off' : 'power'}
                                                />
                                            }
                                        />
                                    )}
                                </Button.Group>
                            </Table.Cell>
                            <Table.Cell>{operationType.code}</Table.Cell>
                            <Table.Cell>{operationType.name}</Table.Cell>
                            <Table.Cell>
                                <Label color={operationType.isActive ? 'green' : 'red'}>
                                    {operationType.isActive ? 'Sim' : 'Não'}
                                </Label>
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>

            <Pagination
                totalPages={totalPages}
                defaultActivePage={filter?.Page ?? 1}
                onPageChange={(e, { activePage }) => setFilter({ ...filter, Page: activePage as number })}
            />
        </Segment>
    );

    return (
        <>
            <Modal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                size="tiny"
                style={{ width: '620px', maxWidth: '95vw', padding: '20px' }}
            >
                <OperationTypeForm
                    isModal
                    open={modalOpen}
                    operationTypeId={editOperationTypeId || undefined}
                    onClose={() => {
                        setModalOpen(false);
                        setEditOperationTypeId(null);
                        setFilter({ ...filter });
                    }}
                />
            </Modal>

            {formContent}
        </>
    );
};

export default OperationTypesList;
